import { lazy } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import MainBlue from 'components/layouts/main-blue';
import OrderConfirmation from 'components/layouts/order-confirmation';
import TailwindLayout from 'components/layouts/tailwind-layout';
import AppRoute from 'routes/app-route';
import useRouter from 'hooks/use-router';
import { encodeQueryString } from 'utils/utils';
import { ROUTE_STACK } from 'hooks/use-path-code';
import suspenseWrapper from 'components/suspense-wrapper';

const GenericNotFound = lazy(() =>
  import(
    /* webpackChunkName: "mipGenericNotFound" */ 'pages/errors/generic-not-found'
  )
);

const IntakeForm = lazy(() =>
  import(/* webpackChunkName: "intake" */ 'pages/mip/intake/intake')
);

const CalendlyEmbed = lazy(() =>
  import(
    /* webpackChunkName: "calendly-embed" */ 'pages/calendly/calendly-embed'
  )
);
const CalendlyExternal = lazy(() =>
  import(
    /* webpackChunkName: "calendly-external" */ 'pages/calendly/calendly-external'
  )
);
const IntakeFormFinish = lazy(() =>
  import(
    /* webpackChunkName: "intakeFinish" */ 'pages/mip/intake/intake-form-finish'
  )
);
const IntakeFormConsent = lazy(() =>
  import(
    /* webpackChunkName: "intakeConsent" */ 'pages/mip/intake/intake-form-consent'
  )
);

const ContinuousForm = lazy(() =>
  import(
    /* webpackChunkName: "continuousForm" */ 'pages/mip/continuous/continuous-form'
  )
);

const DeadEndAnswer = lazy(() =>
  import(/* webpackChunkName: "deadEnd" */ 'pages/dead-end/dead-end')
);

const EDCheckout = lazy(() =>
  import(/* webpackChunkName: "edCheckout" */ 'pages/mip/ed/checkout/checkout')
);

const CHRTConfirmation = lazy(() =>
  import(
    /* webpackChunkName: "chrtConfirmation" */ 'pages/mip/continuous/chrt/chrt-confirmation'
  )
);

const ContinuousFinish = lazy(() =>
  import(
    /* webpackChunkName: "chrtError" */ 'pages/mip/continuous/continuous-finish'
  )
);

const ConfirmationPage = lazy(() =>
  import(
    /* webpackChunkName: "confirmationPage" */ 'pages/universal-upload/confirmation-page'
  )
);
const TailwindConfirmationPage = lazy(() =>
  import(
    /* webpackChunkName: "confirmationPage" */ 'pages/mip/ed/confirmation/confirmation-tailwind'
  )
);

const WaitingToPurchase = lazy(() =>
  import(
    /* webpackChunkName: "waitingToPurchase" */ 'pages/mip/hrt/order-confirmation/waiting-to-purchase-page'
  )
);

const edRoutes = ROUTE_STACK.ED.join('|');
const hrtRoutes = ROUTE_STACK.HRT.join('|');

export default function MIPRouter() {
  const { match, queryParams, location } = useRouter();
  const h2FormIdOverride = encodeQueryString({
    ...queryParams,
    formId: 'MIP-H2',
  });
  const a2FormIdOverride = encodeQueryString({
    ...queryParams,
    formId: 'MIP-A2',
  });

  const useTailwindLayout = location.pathname.includes('dailytada2');

  return (
    <Switch>
      <Redirect
        exact
        from='/mip/form/(shrt1|shrt2|hrt)?'
        to={{
          pathname: '/mip/form/shrt',
          search: h2FormIdOverride,
        }}
      />
      <Redirect
        exact
        from='/mip/form/(hsymp|shrt1)'
        to={{
          pathname: '/hormone-assessment/shrt',
          search: h2FormIdOverride,
        }}
      />
      <Redirect
        exact
        from='/mip/form/(var5d)?'
        to={{
          pathname: '/mip/form/dailytada',
          search: a2FormIdOverride,
        }}
      />
      {!queryParams.formId && (
        <Redirect
          exact
          from={`/mip/form/(${edRoutes})?`}
          to={{
            pathname: location.pathname,
            search: a2FormIdOverride,
          }}
        />
      )}
      {!queryParams.formId && (
        <Redirect
          exact
          from={`/mip/form/(${hrtRoutes})?`}
          to={{
            pathname: location.pathname,
            search: h2FormIdOverride,
          }}
        />
      )}
      <Route
        path={`${match.path}/form/:pathCode?`}
        render={suspenseWrapper(IntakeForm)}
      />
      <AppRoute
        path={`${match.path}/finish`}
        component={suspenseWrapper(IntakeFormFinish)}
        layout={MainBlue}
      />
      <AppRoute
        path={`${match.path}/consent/:pathCode?`}
        component={suspenseWrapper(IntakeFormConsent)}
        layout={MainBlue}
      />
      <Route
        path={`${match.path}/cont/:pathCode?`}
        render={suspenseWrapper(ContinuousForm)}
      />
      <AppRoute
        path={`${match.path}/chrt/confirmation/:pathCode?`}
        component={suspenseWrapper(CHRTConfirmation)}
        layout={MainBlue}
        product='hrt'
      />
      <Route
        path={`${match.path}/conf/:pathCode?/:finishType?`}
        component={suspenseWrapper(ContinuousFinish)}
      />
      <AppRoute
        path={`${match.path}/checkout/:pathCode?`}
        component={suspenseWrapper(EDCheckout)}
        layout={useTailwindLayout ? TailwindLayout : MainBlue}
        product='ed'
      />
      <AppRoute
        path={`${match.path}/deadend/:pathCode?`}
        component={suspenseWrapper(DeadEndAnswer)}
        layout={MainBlue}
      />
      <Route path={`${match.path}/checkout`} component={EDCheckout} />
      <Route
        path={`${match.path}/calendly/:pathCode/:pageId?`}
        render={suspenseWrapper(CalendlyEmbed)}
      />
      <AppRoute
        path={`${match.path}/confirmation/:pathCode`}
        component={suspenseWrapper(
          useTailwindLayout ? TailwindConfirmationPage : ConfirmationPage
        )}
        layout={useTailwindLayout ? TailwindLayout : MainBlue}
        product='ed'
      />
      <Route
        path={`${match.path}/appointment/:appointmentId/:pageId?`}
        render={suspenseWrapper(CalendlyExternal)}
      />
      <Redirect
        from={`${match.path}/verifyId/Upload/:pathCode?`}
        to={{
          pathname: '/upload/shrt',
          search: location.search,
        }}
      />
      <AppRoute
        path={`${match.path}/order-confirmation/:pathCode?`}
        component={suspenseWrapper(WaitingToPurchase)}
        layout={OrderConfirmation}
        product='hrt'
      />
      <AppRoute
        component={suspenseWrapper(GenericNotFound)}
        layout={MainBlue}
      />
    </Switch>
  );
}
